import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { AccountRole } from '@dca/shared';

import { Card } from '../components/Ui.tsx';
import { useAuth } from '../lib/auth.tsx';

const ROLE_CHOICES: Array<{ role: AccountRole; title: string; detail: string }> = [
  {
    role: 'store_owner',
    title: 'I run a shop',
    detail: 'Analytics for your chestshops - pricing, stock, competitors. Free to start.',
  },
  {
    role: 'buyer',
    title: 'I want to buy',
    detail: 'Find who sells what, cheapest first, with live stock. Always free.',
  },
  {
    role: 'delivery',
    title: 'I want delivery work',
    detail: 'Paid jobs checking items and naming variants.',
  },
];

export function LoginPage() {
  const { login, register } = useAuth();
  // The landing page has two calls to action - one for sellers, one for
  // buyers - so it says which it was, and the form opens on sign-up with that
  // role already chosen rather than making the reader pick it twice.
  const [params] = useSearchParams();
  const wanted = params.get('role');
  const preset: AccountRole | null =
    wanted === 'store_owner' || wanted === 'buyer' || wanted === 'delivery' ? wanted : null;

  const [mode, setMode] = useState<'login' | 'register'>(preset ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [discord, setDiscord] = useState('');
  const [role, setRole] = useState<AccountRole>(preset ?? 'store_owner');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') await login(email, password);
      else await register(email, password, role, discord || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="brand" style={{ justifyContent: 'center', paddingBottom: 20 }}>
          <span className="brand-mark">CA</span>
          ChestShop Analytics
        </div>

        <Card title={mode === 'login' ? 'Sign in' : 'Create your account'}>
          <p className="muted small">
            Market intelligence that helps DemocracyCraft shop owners sell more. Connect a firm
            account, keep your own API key, and see where you are mispriced.
          </p>

          <form onSubmit={submit}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                required
                minLength={mode === 'register' ? 10 : 1}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {mode === 'register' && (
                <div className="small muted" style={{ marginTop: 4 }}>
                  At least 10 characters.
                </div>
              )}
            </div>

            {mode === 'register' && (
              <>
                <div className="field">
                  <label>What are you here for?</label>
                  {/* Role decides which tabs exist at all. A buyer has no shop,
                      so showing them analytics would be clutter, not an upsell. */}
                  <div className="role-choice">
                    {ROLE_CHOICES.map((choice) => (
                      <button
                        type="button"
                        key={choice.role}
                        className={`role-option ${role === choice.role ? 'active' : ''}`}
                        onClick={() => setRole(choice.role)}
                      >
                        <strong>{choice.title}</strong>
                        <span className="small muted">{choice.detail}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="discord">Discord handle (optional)</label>
                  <input id="discord" value={discord} onChange={(e) => setDiscord(e.target.value)} />
                </div>
              </>
            )}

            {error && <div className="error-text">{error}</div>}

            <button className="primary" style={{ width: '100%', marginTop: 8 }} disabled={busy}>
              {busy ? 'Working...' : mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
          </form>

          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <button
              className="ghost"
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setError(null);
              }}
            >
              {mode === 'login' ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
            </button>
          </div>
        </Card>

      </div>
    </div>
  );
}
